import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { Wordmark } from "@/components/brand/BrandAssets";
import { trackPurchase } from "@/lib/ads";

const T = {
  confirmed: { lv: "Paldies! Pasūtījums pieņemts", ru: "Спасибо! Заказ принят", en: "Thank you! Order confirmed" },
  orderId: { lv: "Pasūtījuma numurs", ru: "Номер заказа", en: "Order number" },
  orderTime: { lv: "Pasūtīts", ru: "Оформлен", en: "Placed" },
  pickupDetails: { lv: "Līdzņemšana", ru: "Самовывоз", en: "Pickup" },
  deliveryDetails: { lv: "Piegāde", ru: "Доставка", en: "Delivery" },
  name: { lv: "Vārds", ru: "Имя", en: "Name" },
  phone: { lv: "Tālrunis", ru: "Телефон", en: "Phone" },
  email: { lv: "E-pasts", ru: "E-mail", en: "Email" },
  address: { lv: "Adrese", ru: "Адрес", en: "Address" },
  pickupAt: { lv: "Saņemt", ru: "Забрать", en: "Pick up at" },
  readyBy: { lv: "Gatavs", ru: "Будет готов", en: "Ready" },
  deliveryBy: { lv: "Piegāde", ru: "Доставка", en: "Delivery" },
  readyIn: { lv: "20–30 minūšu laikā", ru: "через 20–30 минут", en: "in 20–30 minutes" },
  deliverIn: { lv: "30–45 minūšu laikā", ru: "через 30–45 минут", en: "in 30–45 minutes" },
  at: { lv: "plkst.", ru: "в", en: "at" },
  notes: { lv: "Piezīmes", ru: "Комментарий", en: "Notes" },
  items: { lv: "Pasūtījums", ru: "Состав заказа", en: "Your order" },
  total: { lv: "Kopā", ru: "Итого", en: "Total" },
  payment: { lv: "Apmaksa", ru: "Оплата", en: "Payment" },
  cash: { lv: "Skaidrā naudā saņemot", ru: "Наличными при получении", en: "Cash on delivery" },
  card: { lv: "Ar karti", ru: "Картой", en: "Card" },
  verifying: { lv: "Pārbaudām maksājumu…", ru: "Проверяем оплату…", en: "Verifying payment…" },
  paid: { lv: "Maksājums saņemts", ru: "Оплата прошла успешно", en: "Payment successful" },
  timeout: { lv: "Maksājuma pārbaude aizņem ilgāk nekā parasti. Pasūtījums ir pieņemts.", ru: "Проверка оплаты занимает больше времени. Заказ принят.", en: "Payment verification is taking longer than expected. Your order is confirmed." },
  error: { lv: "Maksājuma pārbaudes kļūda. Lūdzu, sazinieties ar mums.", ru: "Ошибка проверки оплаты. Пожалуйста, свяжитесь с нами.", en: "Payment verification error. Please contact us." },
  pickupMsg: { lv: "Jūsu pasūtījums būs gatavs līdzņemšanai aptuveni 20–30 minūšu laikā.", ru: "Ваш заказ будет готов к самовывозу примерно через 20–30 минут.", en: "Your order will be ready for pickup in approximately 20–30 minutes." },
  deliveryMsg: { lv: "Jūsu pasūtījums tiks piegādāts aptuveni 30–45 minūšu laikā.", ru: "Ваш заказ будет доставлен примерно через 30–45 минут.", en: "Your order will be delivered in approximately 30–45 minutes." },
  scheduledPickup: { lv: "Jūsu pasūtījums būs gatavs līdzņemšanai", ru: "Ваш заказ будет готов к самовывозу", en: "Your order will be ready for pickup" },
  scheduledDelivery: { lv: "Jūsu pasūtījums tiks piegādāts", ru: "Ваш заказ будет доставлен", en: "Your order will be delivered" },
  backToMenu: { lv: "Atpakaļ uz ēdienkarti", ru: "Вернуться в меню", en: "Back to menu" },
  questions: { lv: "Jautājumi? Zvaniet", ru: "Вопросы? Звоните", en: "Questions? Call us" },
  loading: { lv: "Ielādē…", ru: "Загрузка…", en: "Loading…" },
  notFound: { lv: "Pasūtījums nav atrasts", ru: "Заказ не найден", en: "Order not found" },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const language = localStorage.getItem("language") || "lv";
  const t = (k) => T[k][language] || T[k].lv;

  useEffect(() => {
    fetchOrder();
    checkPaymentStatus();
  }, [orderId]);

  useEffect(() => {
    trackPurchase(order);
  }, [order]);

  const fetchOrder = async () => {
    try {
      // React Router decodes URL params automatically, so %23118 becomes #118
      // We need to re-encode it for the API call to prevent # being treated as URL fragment
      const encodedOrderId = encodeURIComponent(orderId);
      console.log('Original orderId from params:', orderId);
      console.log('Re-encoded for API:', encodedOrderId);
      console.log('API URL:', `${API}/orders/${encodedOrderId}`);
      
      const response = await axios.get(`${API}/orders/${encodedOrderId}`);
      console.log('Order fetched successfully');
      setOrder(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching order:", error);
      console.error("Error response:", error.response);
      console.error("Order ID was:", orderId);
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    // Get session_id from URL if exists
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId && !polling) {
      setPolling(true);
      setPaymentStatus('checking');
      await pollPaymentStatus(sessionId, 0);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setPolling(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        setPolling(false);
        // Refresh order to get updated payment status
        fetchOrder();
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        setPolling(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setPolling(false);
    }
  };

  if (loading) {
    return (
      <div className="rz-page min-h-screen flex items-center justify-center">
        <p className="rz-eyebrow">{t("loading")}</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="rz-page min-h-screen flex items-center justify-center">
        <p className="font-display text-2xl text-[#1C1917]">{t("notFound")}</p>
      </div>
    );
  }

  const formatOrderTime = (dateString) =>
    new Date(dateString).toLocaleString('en-GB', { timeZone: 'Europe/Riga', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });

  const formatDeliveryTime = (deliveryTime, deliveryType) => {
    if (!deliveryTime) return deliveryType === 'pickup' ? t("readyIn") : t("deliverIn");
    const date = new Date(deliveryTime);
    const time = date.toLocaleTimeString('en-GB', { timeZone: 'Europe/Riga', hour: '2-digit', minute: '2-digit', hour12: false });
    const dateStr = date.toLocaleDateString('en-GB', { timeZone: 'Europe/Riga', day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${dateStr} ${t("at")} ${time}`;
  };

  const isPickup = order.delivery_type === 'pickup';
  const Row = ({ label, value, testId }) => (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-3 border-b border-[#E7E5E4]" data-testid={testId}>
      <span className="sm:w-36 shrink-0 text-[0.68rem] font-medium tracking-[0.22em] uppercase text-[#78716C]">{label}</span>
      <span className="text-[#1C1917]">{value}</span>
    </div>
  );

  return (
    <div className="rz-page min-h-screen font-body" data-testid="order-confirmation-page">
      <div className="max-w-[820px] mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex justify-center mb-12"><Link to="/" aria-label="Roze di Napoli"><Wordmark size="nav" tone="dark" /></Link></div>

        <div data-testid="order-confirmation-card">
          <div className="text-center mb-14">
            <CheckCircle2 className="w-10 h-10 text-[#1C1917] mx-auto mb-6" strokeWidth={1} data-testid="success-icon" />
            <h1 className="font-display text-[2.2rem] sm:text-[3rem] leading-[1.05] text-[#1C1917] m-0" data-testid="confirmation-title">
              {t("confirmed")}
            </h1>
            <p className="mt-6 text-[0.72rem] font-medium tracking-[0.22em] uppercase text-[#78716C] m-0" data-testid="order-id-text">
              {t("orderId")} <span className="text-[#1C1917]">#{order.id.replace(/^#/, "").slice(0, 8).toUpperCase()}</span>
            </p>
            {order.created_at && (
              <p className="text-sm text-[#A8A29E] mt-2 m-0" data-testid="order-time">{t("orderTime")}: {formatOrderTime(order.created_at)}</p>
            )}
          </div>

          <div className="bg-[#F5F2EC] border border-[#E7E5E4] p-6 sm:p-8 text-center mb-12">
            <p className="font-display text-[1.25rem] sm:text-[1.45rem] text-[#1C1917] m-0 leading-[1.5]" data-testid="delivery-time-message">
              {order.delivery_time
                ? `${isPickup ? t("scheduledPickup") : t("scheduledDelivery")} ${formatDeliveryTime(order.delivery_time, order.delivery_type)}`
                : (isPickup ? t("pickupMsg") : t("deliveryMsg"))}
            </p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-[1.7rem] text-[#1C1917] m-0 mb-4" data-testid="delivery-details-heading">
              {isPickup ? t("pickupDetails") : t("deliveryDetails")}
            </h2>
            <div className="border-t border-[#E7E5E4]">
              <Row label={t("name")} value={order.customer_name} testId="customer-name" />
              <Row label={t("phone")} value={order.customer_phone} testId="customer-phone" />
              {order.customer_email && <Row label={t("email")} value={order.customer_email} testId="customer-email" />}
              {!isPickup && order.delivery_address && <Row label={t("address")} value={order.delivery_address} testId="delivery-address" />}
              {isPickup && <Row label={t("pickupAt")} value="Liepu iela 9, Liepāja, LV-3401" testId="pickup-address" />}
              <Row label={isPickup ? t("readyBy") : t("deliveryBy")} value={formatDeliveryTime(order.delivery_time, order.delivery_type)} testId="delivery-time" />
              {order.notes && <Row label={t("notes")} value={<span className="italic text-[#57534E]">{order.notes}</span>} testId="order-notes" />}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-[1.7rem] text-[#1C1917] m-0 mb-4" data-testid="order-items-heading">{t("items")}</h2>
            <div className="border-t border-[#E7E5E4]">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-start gap-6 py-4 border-b border-[#E7E5E4]" data-testid={`order-item-${index}`}>
                  <div className="min-w-0">
                    <p className="font-display text-[1.2rem] text-[#1C1917] m-0" data-testid={`item-name-${index}`}>{item.name}</p>
                    <p className="text-sm text-[#78716C] m-0 mt-1 tabular-nums" data-testid={`item-quantity-${index}`}>{item.quantity} × €{item.price.toFixed(2)}</p>
                    {item.notes && <p className="text-sm text-[#78716C] mt-1 m-0 italic" data-testid={`item-notes-${index}`}>{item.notes}</p>}
                  </div>
                  <p className="font-medium text-[#1C1917] tabular-nums m-0" data-testid={`item-price-${index}`}>€{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-baseline pt-6">
              <span className="rz-eyebrow !text-[#1C1917]" data-testid="total-label">{t("total")}</span>
              <span className="font-display text-[2rem] leading-none text-[#1C1917] tabular-nums" data-testid="total-amount">€{order.total_amount.toFixed(2)}</span>
            </div>
            <p className="text-sm text-[#78716C] mt-3 m-0" data-testid="payment-method">
              {t("payment")}: {order.payment_method === 'cash' ? t("cash") : t("card")}
            </p>

            {paymentStatus && (
              <div className="mt-5">
                {paymentStatus === 'checking' && (
                  <div className="rz-note flex items-center gap-3"><div className="animate-spin rounded-full h-4 w-4 border-b border-[#1C1917]"></div><span>{t("verifying")}</span></div>
                )}
                {paymentStatus === 'paid' && (
                  <div className="rz-note rz-note--ok flex items-center gap-3"><CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /><span>{t("paid")}</span></div>
                )}
                {paymentStatus === 'timeout' && <div className="rz-note">{t("timeout")}</div>}
                {paymentStatus === 'error' && <div className="rz-note rz-note--warn">{t("error")}</div>}
              </div>
            )}
          </section>

          <Link to="/" className="rz-btn w-full" data-testid="back-to-menu-button">
            {t("backToMenu")}
          </Link>
        </div>

        <p className="text-center text-sm text-[#78716C] mt-10 m-0">
          {t("questions")} <a href="tel:+37128717331" className="rz-link text-[#1C1917]">+371 28717331</a> · Liepu iela 9, Liepāja
        </p>
      </div>
    </div>
  );
};

export default OrderConfirmation;